import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle } from 'lucide-react';
import Seo from './Seo';

const Terms = () => {
  const legalPhoneDisplay = '630-799-1556';
  const legalPhoneHref = '+16307991556';

  const [copied, setCopied] = useState(false);

  const copyLegalNumber = async () => {
    try {
      await navigator.clipboard.writeText(legalPhoneDisplay);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Terms of Service"
        description="Terms of Service for Libsys Inc — service warranties, user obligations, dispute resolution, and legal terms governing use of the Libsys website and services."
        path="/terms-of-service"
      />
      {/* Hero Header */}
      <section className="relative pt-20 pb-12 bg-primary overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-[120px] -mr-40 -mt-40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#e02424] text-[12px] font-black uppercase tracking-[0.2em] mb-6">
              <Shield className="w-4 h-4" />
              Legal Portal
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
              Terms and <span className="text-secondary text-glow">Conditions</span>
            </h1>
            <p className="text-blue-100/75 text-base md:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
              Last updated: March 23, 2026. Please read these terms carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-none"
          >
            {[
              {
                title: "1. Agreement",
                content: "These Terms and Conditions (\"Terms\") govern your access to and use of the Libsys Inc. website (\"Website\"). By accessing or using the Website, you agree to be bound by these Terms in their entirety, as they may be modified by Libsys from time to time. We recommend you review these Terms periodically for any updates."
              },
              {
                title: "2. Information and Content",
                content: "Libsys strives to provide accurate information on the Website, including names, images, and other materials (collectively, \"Content\"). However, we make no representations, endorsements, or warranties that the Content is error-free or suitable for any particular purpose. The Website and its Content are provided \"as is\" and at your own risk. We disclaim all representations, warranties, and conditions, express or implied, including but not limited to warranties of title, accuracy, merchantability, fitness for a particular purpose, and non-infringement."
              },
              {
                title: "3. Limitation of Liability",
                content: "Libsys shall not be liable for any damages arising from your use of the Website, including, but not limited to, indirect, incidental, special, consequential, or punitive damages. This applies regardless of the legal theory under which damages are sought (contract, tort, or otherwise) and even if Libsys is aware of the possibility of such damages."
              },
              {
                title: "4. Content Use",
                content: "The Content on this Website is intended for your personal, non-commercial use only. You may not copy, modify, distribute, transmit, display, perform, reproduce, transfer, resell, or republish any Content without Libsys' prior written consent."
              },
              {
                title: "5. Intellectual Property",
                content: "Except as otherwise noted, all Content on the Website, including white papers, case studies, graphics, icons, and the overall appearance of the Website, are the sole and exclusive property of Libsys."
              },
              {
                title: "6. Third-Party Links",
                content: "The Website may provide links to third-party websites. Libsys has no control over these websites, their content, or the products/services they offer. Links do not constitute an endorsement or warranty by Libsys for these websites or their content. Libsys disclaims responsibility for the privacy policies and practices of such third-party websites."
              },
              {
                title: "7. Modifications",
                content: "Libsys reserves the right to modify these Terms at any time without prior notice. Your continued use of the Website following any modifications constitutes your acceptance of the revised Terms."
              },
              {
                title: "8. Forward-Looking Statements",
                content: "This Website may contain \"forward-looking statements\" within the meaning of applicable securities laws. These statements involve risks, uncertainties, and other factors that could cause actual results to differ materially from those projected. Libsys does not undertake any obligation to update any forward-looking statements."
              },
              {
                title: "9. Disclaimers",
                content: (
                  <div className="space-y-4">
                    <p>While Libsys takes reasonable precautions to ensure secure access to the Website, we cannot guarantee it is entirely free from viruses or other malicious software. You are responsible for taking appropriate security measures before and during your use of the Website.</p>
                    <p>Libsys disclaims all warranties regarding the Content on the Website. We encourage you to verify the information from independent sources before relying on it. We assume no responsibility for updates, or any actions/inactions based on information on this Website. You agree to indemnify Libsys against any claims arising from your misuse of the Website's content or services.</p>
                  </div>
                )
              },
              {
                title: "10. Contact Us",
                content: (
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 mt-8">
                    <p className="font-bold text-slate-900 mb-4">If you have any questions regarding these Terms, please contact Libsys.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">Contact Email</p>
                        <a href="mailto:Info@LibsysInc.Com" className="text-primary font-bold hover:text-secondary transition-colors">Info@LibsysInc.Com</a>
                      </div>
                      <div>
                        <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">Contact Location</p>
                        <p className="text-slate-600 font-medium">Libsys Inc, 2020 Calamos Ct, Suite # 200, Naperville IL 60563, USA</p>
                      </div>
                    </div>
                  </div>
                )
              }
            ].map((section, i) => (
              <div key={i} className="mb-6 last:mb-0 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-blue-50 text-primary flex items-center justify-center text-sm">
                    {i + 1}
                  </span>
                  {section.title.split('. ')[1] || section.title}
                </h2>
                <div className="text-slate-600 text-[16px] md:text-[17px] leading-8 font-medium pl-11">
                  {section.content}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-slate-50 py-20 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100/50 text-primary text-[11px] font-black uppercase tracking-[0.2em] mb-8">
                <CheckCircle className="w-4 h-4" />
                Agreed and Accepted
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-6">Ready to move forward?</h2>
            <p className="text-slate-500 max-w-xl mx-auto mb-10 text-lg font-medium">
                Our team is ready to help you transform your business with innovative IT solutions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
                <a href={`tel:${legalPhoneHref}`} className="px-8 py-4 bg-primary text-white font-black text-[14px] uppercase tracking-wider rounded-xl hover:bg-primary/90 transition-all shadow-xl shadow-primary/20">
                    Contact Our Legal Team: 630-799-1556
                </a>
                <button
                  type="button"
                  onClick={copyLegalNumber}
                  className="px-8 py-4 bg-white text-primary border border-primary/20 font-black text-[14px] uppercase tracking-wider rounded-xl hover:bg-primary/5 transition-all w-48"
                >
                  {copied ? 'Copied!' : 'Copy Number'}
                </button>
            </div>
            <p className="text-slate-400 text-sm mt-4">
              If call pop-up does not appear on desktop, use the copied number to dial: {legalPhoneDisplay}
            </p>
        </div>
      </section>
    </div>
  );
};

export default Terms;
